import * as React from "react";
import { ActivityStateMixin, ActivityStateMixinState } from "../../activities/ActivityStateMixin";
import * as API from "../../API";
import { isLoading, Loading, loading } from "../../Loading";
import { SimplifiedAgencies } from "../../messages/Agency";
import { AgencyId } from "../../messages/ObjectIds";
import { retryableModalNetworkRequest } from "../../NetworkRequest";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { translate } from "../../translate";
import { getAnomalies } from "../CaseSettings/utils/getAnomalies";
import { simpleAlert } from "../SimpleAlert";
import { AgencyRequestScreen, AgencyRequestScreenData } from "./AgencyRequestScreen";

interface AgencyRequestActivityProps {
  api: API.API;
  appState: AppState;
  onBackClick: () => void;
  caregiver: AppCaregiver | Loading;
  onFinishRequest: () => void;
}

interface AgencyRequestActivityState {
  data: AgencyRequestScreenData;
  mixin: ActivityStateMixinState;
}

export class AgencyRequestActivity extends React.Component<
  AgencyRequestActivityProps,
  AgencyRequestActivityState
> {
  constructor(props: AgencyRequestActivityProps) {
    super(props);

    this.activityStateMixin = new ActivityStateMixin(
      (state) =>
        this.setState((_prevState: AgencyRequestActivityState) => ({
          mixin: state,
        })),
      this.props.appState
    );

    this.state = {
      data: {
        type: "Empty",
      },
      mixin: this.activityStateMixin.initialState(),
    };
  }

  private activityStateMixin: ActivityStateMixin;

  componentWillUnmount() {
    this.activityStateMixin.componentWillUnmount();
  }

  handleAgencySearch(code: string): Promise<SimplifiedAgencies | null> {
    return this.props.api
      .getAgencyFromCode(code)
      .then(this.handleAgencySearchSuccess)
      .catch(this.handleAgencySearchFailure);
  }

  private handleAgencySearchSuccess = (agency: SimplifiedAgencies) => {
    this.setState({
      data: {
        type: "ShowAgency",
        agency: agency,
      },
    });

    return agency;
  };

  private handleAgencySearchFailure = (response: any) => {
    if (response instanceof API.StatusCodeError) {
      this.setState({
        data: {
          type: "ShowError",
          errorMessage:
            response.statusCode === 404
              ? translate(STRINGS.WE_COULDNT_FIND_YOUR_AGENCY)
              : translate(STRINGS.SOMETHING_WENT_WRONG),
        },
      });

      return null;
    } else {
      throw response;
    }
  };

  async handleAgencyRequest(id: AgencyId): Promise<void> {
    const result = await retryableModalNetworkRequest(async () => {
      try {
        await this.props.api.setAgencyRequested(id);
        return true;
      } catch (err) {
        switch (err.statusCode) {
          case 409:
            simpleAlert({
              icon: "error",
              title: translate(STRINGS.CANT_REQUEST_JOIN_AGENCY),
              message: translate(STRINGS.PLEASE_CONTACT_THE_AGENCY_FOR_MORE_DETAILS),
            });
            return false;
          default: {
            throw err;
          }
        }
      }
    }, true);

    if (result === true) {
      this.props.onFinishRequest();
    }
  }

  handleOnAgencyReset() {
    this.setState({
      data: {
        type: "Empty",
      },
    });
  }

  render() {
    return (
      <AgencyRequestScreen
        data={this.state.data}
        caregiver={this.props.caregiver}
        agencies={
          isLoading(this.state.mixin.appAgenciesState)
            ? loading()
            : this.state.mixin.appAgenciesState.agencies
        }
        getAnomalies={() => getAnomalies(this.props.api)}
        onAgencySearch={(code) => this.handleAgencySearch(code)}
        onAgencyRequest={(id) => this.handleAgencyRequest(id)}
        onAgencyReset={() => this.handleOnAgencyReset()}
        onBackClick={() => this.props.onBackClick()}
      />
    );
  }
}
